/* =============================================================================
   STATIC SALE — the shop half of the auction hub (2026-10-01)
   =============================================================================
   A static sale (`events.event_type = 'static'`) renders the same hub page with
   a price and an Add button instead of a bid bar, a sticky Checkout pill once
   anything is in the cart, and a drawer to review it. Checkout mints the order
   and hands straight to the ordinary custom checkout — there is no invoice step
   and nobody at the desk is involved.

   ⚠️ THE CART IS SERVER STATE, NOT A LIST IN THIS FILE. Every action POSTs and
   re-renders from the reply, so this module never keeps its own idea of what is
   in the cart — a localStorage cart would disagree with the server about a
   one-of-a-kind object.

   ⚠️ ADDING RESERVES NOTHING (multi-cart). As many shoppers as like may hold the
   same lot and the first to PAY wins; the only thing that locks one is pressing
   Checkout, which takes a 20-minute reservation server-side. There is therefore
   NO cart timer on screen — see "no cart clock" below.

   ⚠️ AND IT NEVER COMPUTES MONEY. Prices, shipping and the total all come from
   the server on every response (lib/static-shipping.js). A shop whose client
   could name the price is a shop that gives things away — and a second copy of
   the shipping rules here is a second copy that would drift.

   ⚠️ NOT LOADED ON AN AUCTION. The section ships this file only when the
   collection's `sale_mode` metafield says `static`, and ships the BIDDING
   scripts only when it does not — the same "do not ship the code" approach the
   preview mode uses, because no bid button can be left behind if no bidding
   code is on the page.
   ============================================================================= */
(function () {
  'use strict';

  var CFG = window.MHV_STATIC_SALE_CONFIG || {};
  var CODE = CFG.sessionCode || '';
  if (!CODE) return;

  var API = (window.MHV && MHV.SERVER_URL ? MHV.SERVER_URL : '') + '/api/static-sale/' + encodeURIComponent(CODE);

  // The server is the authority on all of this; these are only the last answer.
  var sale = { lots: {}, bySku: {}, shipping: { base: 0, additional: 0 } };
  var cart = { lines: [], subtotal: 0, shipping: 0, total: 0, shippingIsEstimate: true };
  var busy = {};            // product id → an add/remove is in flight
  var drawerOpen = false;

  function $(id) { return document.getElementById(id); }
  function money(n) {
    var v = Number(n) || 0;
    return '$' + v.toFixed(2);
  }
  function esc(s) {
    return (window.MHV && MHV.escapeHtml) ? MHV.escapeHtml(String(s == null ? '' : s)) : String(s == null ? '' : s);
  }

  /* One fetch helper. mhvFetch injects X-Customer-Key and waits for key
     provisioning, which is what makes these endpoints work at all for a logged-in
     shopper — a bare fetch() would be anonymous and 401. */
  function api(method, path, body) {
    var url = API + (path || '');
    var opts = { method: method, headers: { 'Content-Type': 'application/json' } };
    if (body) opts.body = JSON.stringify(body);
    var f = window.mhvFetch || window.fetch;
    return f(url, opts, { useCache: false, dedupe: method === 'GET' })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, status: r.status, data: d || {} }; }); })
      .catch(function () { return { ok: false, status: 0, data: {} }; });
  }

  function signedIn() { return !!CFG.customerId; }

  // ===========================================================================
  // PAINTING A CARD
  // ===========================================================================
  /* ⚠️ A LOT WE HAVE NO ENTRY FOR STAYS DISABLED, and its note says so. The API
     answers a failure with an empty map on purpose (so an outage cannot blank
     the page), which means "absent" has to read as "not known to be available"
     rather than "available" — the one direction that cannot sell something
     twice. */
  function paintCard(productId) {
    var entry = sale.lots[String(productId)];
    var priceEl = $('static_price_' + productId);
    var shipEl = $('static_ship_' + productId);
    var btn = $('static_add_' + productId);
    var noteEl = $('static_note_' + productId);
    if (!btn) return;

    var inCart = cart.lines.some(function (l) { return String(l.productId || '') === String(productId) || (entry && l.itemId === entry.itemId); });

    if (priceEl && entry && entry.price != null) priceEl.textContent = money(entry.price);

    if (shipEl) {
      if (entry && entry.staticShipping != null) {
        shipEl.hidden = false;
        shipEl.textContent = entry.staticShipping > 0
          ? ('+ ' + money(entry.staticShipping) + ' shipping')
          : 'Free shipping';
      } else {
        shipEl.hidden = true;
      }
    }

    /* ── "IN N OTHER PEOPLE'S CARTS" ──────────────────────────────────────
       A static sale is MULTI-CART: several shoppers can hold the same
       one-of-a-kind item and the first to PAY wins, so this is a real reason to
       hurry rather than decoration.
       ⚠️ OTHERS, not everyone — the viewer is in the server's count, and a
       shopper who holds it being told "in 3 people's carts" reads as four
       rivals. ⚠️ And silent at zero. */
    var urgEl = $('static_urgency_' + productId);
    if (urgEl) {
      var others = entry ? Math.max(0, (Number(entry.cartCount) || 0) - (inCart ? 1 : 0)) : 0;
      /* ⚠️ "Someone is checking out with this" OUTRANKS the cart count — "in 2
         other people's carts" understates a thing that is one card-tap from
         being gone. It is still addable: the reservation can lapse, and
         refusing the add would take the item off sale, which is what this
         design exists to avoid. */
      var urgText = (entry && entry.checkingOut && !inCart) ? 'Someone is checking out with this'
        : (others < 1 ? null
          : (others === 1 ? "In 1 other person's cart" : 'In ' + others + " other people's carts"));
      if (urgText && entry && entry.available) {
        urgEl.hidden = false;
        urgEl.textContent = urgText;
      } else {
        urgEl.hidden = true;
      }
    }

    var label = btn.querySelector('.hub-static-add-label') || btn;
    btn.classList.remove('is-in-cart', 'is-gone', 'is-reserved', 'is-unsold');

    if (busy[productId]) {
      btn.disabled = true;
      label.textContent = '…';
      return;
    }
    if (inCart) {
      btn.disabled = false;
      btn.classList.add('is-in-cart');
      label.textContent = 'In Cart — Remove';
      if (noteEl) noteEl.hidden = true;
      return;
    }
    if (!entry) {
      btn.disabled = true;
      label.textContent = 'Add to Cart';
      if (noteEl) noteEl.hidden = true;
      return;
    }
    /* ⚠️ TWO THINGS HAVE BEEN CALLED "RESERVED" HERE, AND ONLY ONE IS A STATE.
       Being in somebody else's cart stopped blocking anything on 2026-10-02
       (multi-cart) — that is the urgency line above, and `entry.checkingOut`.
       RESERVED came back on 2026-10-03 as something different: a lot taken off
       sale by hand or by an invoice somebody sent. It has NO clock, so unlike
       every other scarcity signal on this card it will not come back on its own,
       and the server has already refused it (`available` false, reason
       `reserved`). */
    if (!entry.available) {
      btn.disabled = true;
      btn.classList.add('is-gone');
      /* ⚠️ UNSOLD IS NOT SOLD (2026-10-24). An item folded into another sale is
         `passed` here and the server answers reason `unsold`: nobody bought it,
         and saying "Sold" would tell a shopper somebody did. Grey and dead like
         Sold — it cannot be bought here — but the true word. */
      label.textContent = entry.reason === 'sold' ? 'Sold'
        : (entry.reason === 'unsold' ? 'Unsold'
          : (entry.reason === 'reserved' ? 'Reserved' : 'Not for sale'));
      if (entry.reason === 'unsold') btn.classList.add('is-unsold');
      /* ⚠️ RESERVED IS AMBER, NOT GREY (owner, 2026-10-03: "lets have it say
         reserved, and be yellow"). Grey is what SOLD looks like, and the two are
         not the same news: sold is over, reserved is "ask us". The amber is the
         one already used for scarcity on this card, so the colour means the same
         thing in both places. */
      if (entry.reason === 'reserved') btn.classList.add('is-reserved');
      /* ⚠️ A RESERVED LOT SAYS SO IN FULL. "Reserved" on a dead button reads
         as a system state; the note is what tells a shopper there is somebody to
         ask. Every other unbuyable reason is self-explanatory and stays silent. */
      if (noteEl) {
        if (entry.reason === 'reserved') {
          noteEl.hidden = false;
          noteEl.classList.add('is-reserved');
          noteEl.textContent = 'Reserved — contact us if you are interested';
        } else {
          noteEl.hidden = true;
          noteEl.classList.remove('is-reserved');
        }
      }
      return;
    }
    btn.disabled = false;
    label.textContent = 'Add to Cart';
    if (noteEl) noteEl.hidden = true;
  }

  function paintAll() {
    document.querySelectorAll('[data-static-lot]').forEach(function (el) {
      paintCard(el.getAttribute('data-static-lot'));
    });
    paintPill();
    if (drawerOpen) paintDrawer();
  }

  // ===========================================================================
  // THE STICKY PILL
  // ===========================================================================
  /* ⚠️ IT IS ONLY THERE WHEN THERE IS SOMETHING IN THE CART. A permanent bar at
     the bottom of a 200-lot page is furniture that covers a row of lots on a
     phone for the entire visit. */
  function ensurePill() {
    var pill = $('hub-static-pill');
    if (pill) return pill;
    pill = document.createElement('div');
    pill.id = 'hub-static-pill';
    pill.className = 'hub-static-pill';
    pill.setAttribute('role', 'region');
    pill.setAttribute('aria-label', 'Your cart');
    pill.innerHTML =
      '<button type="button" class="hub-static-pill-main" id="hub-static-pill-open">' +
        '<span class="hub-static-pill-count" id="hub-static-pill-count">0</span>' +
        '<span class="hub-static-pill-text">' +
          '<span class="hub-static-pill-label">Checkout</span>' +
          '<span class="hub-static-pill-total" id="hub-static-pill-total"></span>' +
        '</span>' +
      '</button>';
    document.body.appendChild(pill);
    $('hub-static-pill-open').addEventListener('click', openDrawer);
    return pill;
  }

  function paintPill() {
    var n = cart.lines.length;
    var pill = ensurePill();
    pill.classList.toggle('is-shown', n > 0);
    if (!n) return;
    $('hub-static-pill-count').textContent = String(n);
    $('hub-static-pill-total').textContent = money(cart.subtotal) +
      (cart.shipping > 0 ? ' + ' + money(cart.shipping) + ' ship' : '');
  }

  /* ⚠️ THERE IS NO CART CLOCK, AND THAT IS DELIBERATE (owner, 2026-10-03).
     A cart holds nothing — as many shoppers as like may hold the same lot — so a
     visible countdown was a promise the system does not make. This pill used to
     show a live mm:ss that went red under two minutes, which reads as "your item
     is reserved for you": the exact behaviour multi-cart moved away from.
     The server still expires an abandoned cart, but at 24 hours
     (`lib/static-sale.HOLD_MINUTES`) purely so "in N other people's carts" stays
     honest — housekeeping no shopper ever meets.
     ⚠️ The one window that IS a hold is the CHECKOUT reservation, and it is not
     shown either (owner's call). If a timer is ever wanted again, it belongs
     there and nowhere else. */

  // ===========================================================================
  // THE DRAWER
  // ===========================================================================
  function ensureDrawer() {
    var d = $('hub-static-drawer');
    if (d) return d;
    d = document.createElement('div');
    d.id = 'hub-static-drawer';
    d.className = 'hub-static-drawer';
    d.innerHTML =
      '<div class="hub-static-drawer-scrim" id="hub-static-drawer-scrim"></div>' +
      '<div class="hub-static-drawer-panel" role="dialog" aria-modal="true" aria-label="Your cart">' +
        '<div class="hub-static-drawer-head">' +
          '<h2 class="hub-static-drawer-title">Your Cart</h2>' +
          '<button type="button" class="hub-static-drawer-close" id="hub-static-drawer-close" aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="hub-static-drawer-body" id="hub-static-drawer-body"></div>' +
        '<div class="hub-static-drawer-foot" id="hub-static-drawer-foot"></div>' +
      '</div>';
    document.body.appendChild(d);
    $('hub-static-drawer-close').addEventListener('click', closeDrawer);
    $('hub-static-drawer-scrim').addEventListener('click', closeDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawerOpen) closeDrawer();
    });
    return d;
  }

  function openDrawer() {
    ensureDrawer().classList.add('is-open');
    document.body.classList.add('hub-static-no-scroll');
    drawerOpen = true;
    paintDrawer();
    // Re-read on open: the cart may have changed in another tab, and a lot may
    // have sold at the desk while the drawer was shut.
    refresh();
  }
  function closeDrawer() {
    var d = $('hub-static-drawer');
    if (d) d.classList.remove('is-open');
    document.body.classList.remove('hub-static-no-scroll');
    drawerOpen = false;
  }

  function paintDrawer() {
    var body = $('hub-static-drawer-body');
    var foot = $('hub-static-drawer-foot');
    if (!body || !foot) return;

    if (!cart.lines.length) {
      body.innerHTML = '<p class="hub-static-empty">Nothing in your cart yet.</p>';
      foot.innerHTML = '';
      return;
    }

    var html = '';
    /* Anything the server DROPPED is said first and said plainly. A lot can sell
       at the desk, be comped or be backed out while it sits in a cart, and a
       cart that quietly shrinks is one the shopper assumes they misread. */
    (cart.dropped || []).forEach(function (l) {
      html += '<div class="hub-static-line is-dropped">' +
        '<div class="hub-static-line-main">' +
          '<div class="hub-static-line-title">' + esc(l.title || (l.lot != null ? 'Lot ' + l.lot : 'Item')) + '</div>' +
          '<div class="hub-static-line-note">' + esc(l.message || 'No longer available') + '</div>' +
        '</div></div>';
    });

    cart.lines.forEach(function (l) {
      html += '<div class="hub-static-line">' +
        (l.photo ? '<img class="hub-static-line-img" src="' + esc(l.photo) + '" alt="" loading="lazy">' : '<div class="hub-static-line-img is-empty"></div>') +
        '<div class="hub-static-line-main">' +
          /* ⚠️ NO LOT NUMBER, NO LOT LINE. A static sale is a shop and its items
             need not be numbered, so an empty eyebrow reading "Lot —" is worse
             than no eyebrow: it tells the shopper a number is missing rather
             than that there was never one to show. */
          (l.lot == null || l.lot === '' ? '' : '<div class="hub-static-line-lot">Lot ' + esc(l.lot) + '</div>') +
          '<div class="hub-static-line-title">' + esc(l.title || '') + '</div>' +
          /* ⚠️ The urgency repeated IN THE CART, where it matters most — this is
             the screen somebody is deciding whether to finish on, and the item
             can still be lost to a faster buyer. */
          (l.checkingOutByOther
            ? '<div class="hub-static-line-urgency is-reserved">Someone is checking out with this now</div>'
            : (l.othersInCart > 0
              ? '<div class="hub-static-line-urgency">' +
                  (l.othersInCart === 1 ? '1 other person has this in their cart'
                    : esc(String(l.othersInCart)) + ' other people have this in their cart') +
                '</div>'
              : '')) +
        '</div>' +
        '<div class="hub-static-line-side">' +
          '<div class="hub-static-line-price">' + money(l.price) + '</div>' +
          '<button type="button" class="hub-static-line-remove" data-static-remove="' + esc(l.itemId) + '">Remove</button>' +
        '</div></div>';
    });
    body.innerHTML = html;
    body.querySelectorAll('[data-static-remove]').forEach(function (b) {
      b.addEventListener('click', function () { removeItem(b.getAttribute('data-static-remove')); });
    });

    /* ⚠️ SHIPPING IS LABELLED AN ESTIMATE UNTIL AN ADDRESS IS KNOWN, because it
       is zone-scaled and the server deliberately quotes the UNSCALED (low)
       figure before it knows where the parcel is going. Presenting that as the
       final number would surprise somebody at the last step, which is the one
       place a shop cannot afford a surprise. */
    foot.innerHTML =
      '<div class="hub-static-sum">' +
        '<div class="hub-static-sum-row"><span>Subtotal</span><span>' + money(cart.subtotal) + '</span></div>' +
        '<div class="hub-static-sum-row"><span>Shipping' +
          (cart.shippingIsEstimate ? ' <em>(estimated)</em>' : '') +
        '</span><span>' + money(cart.shipping) + '</span></div>' +
        '<div class="hub-static-sum-row is-total"><span>Total</span><span>' + money(cart.total) + '</span></div>' +
        (cart.shippingIsEstimate
          ? '<p class="hub-static-sum-note">Final shipping is calculated at checkout once you choose an address — or pick up free in person.</p>'
          : '') +
      '</div>' +
      '<button type="button" class="hub-static-checkout" id="hub-static-checkout">Checkout</button>';
    $('hub-static-checkout').addEventListener('click', checkout);
  }

  // ===========================================================================
  // ACTIONS
  // ===========================================================================
  function toast(msg, kind) {
    if (window.MHV && MHV.toast) return MHV.toast(msg, kind || 'info');
    if (window.MHV && MHV.alert) return MHV.alert(msg);
     
    alert(msg);
  }

  /* ⚠️ A SIGNED-OUT SHOPPER IS SENT TO SIGN IN, NOT REFUSED. The cart is a
     server-side reservation against a customer, so there is no anonymous cart to
     fall back on — but the return_url brings them straight back to this sale. */
  function requireSignIn() {
    toast('Please sign in to shop this sale.', 'info');
    var back = encodeURIComponent(location.pathname + location.search);
    setTimeout(function () { location.href = '/account/login?return_url=' + back; }, 900);
  }

  function addItem(productId) {
    if (!signedIn()) return requireSignIn();
    if (busy[productId]) return;
    var entry = sale.lots[String(productId)];
    busy[productId] = true;
    paintCard(productId);
    /* ⚠️ '/cart'. This said '' and POSTed to the collection root — a path with no
       POST route — so every Add answered 404 and the card showed the generic
       "Could not add that to your cart" (a 404 carries no `error` field, so the
       client fell back to its own wording and the real cause was invisible). The
       server's add is POST /api/static-sale/:code/cart. */
    api('POST', '/cart', { productId: productId, itemId: entry ? entry.itemId : undefined })
      .then(function (r) {
        busy[productId] = false;
        if (!r.ok) {
          // The lot went while they were looking at it. Say what happened, then
          // re-read so the card tells the same story as the message.
          toast((r.data && r.data.error) || 'Could not add that to your cart.', 'error');
          return refresh();
        }
        applyCart(r.data);
        if (!drawerOpen) toast('Added to your cart.', 'success');
        return refresh();
      });
  }

  function removeItem(itemId) {
    api('DELETE', '/cart/' + encodeURIComponent(itemId))
      .then(function (r) {
        if (r.ok) applyCart(r.data);
        return refresh();
      });
  }

  function checkout() {
    var btn = $('hub-static-checkout');
    if (btn) { btn.disabled = true; btn.textContent = 'Starting checkout…'; }
    return api('POST', '/checkout')
      .then(function (r) {
        if (r.ok && r.data && r.data.checkoutUrl) {
          location.href = r.data.checkoutUrl;
          return null;
        }
        if (btn) { btn.disabled = false; btn.textContent = 'Checkout'; }
        if (r.data && r.data.cart) applyCart(r.data.cart);
        /* ⚠️ BLOCKED BECAUSE SOMEBODY ELSE IS MID-CHECKOUT — the one failure
           this whole design exists to make answerable. It is a QUESTION, not an
           error: here is what is gone, shall I drop it and buy the rest? A
           toast would throw that away and leave them pressing Checkout again. */
        /* ⚠️ `in-checkout`, NOT `reserved` (server renamed it 2026-10-03). The
           server's `reserved` now means a lot taken off sale for good, which this
           branch's "remove them and check out with the rest" would be the wrong
           answer to — that item is not coming back. */
        if (r.status === 409 && r.data && r.data.reason === 'in-checkout') {
          showConflict(r.data);
          return null;
        }
        toast((r.data && r.data.error) || 'Could not start checkout. Please try again.', 'error');
        return refresh();
      });
  }

  /* The blocked-checkout sheet. Built fresh each time rather than reused: it is
     rare, it is short-lived, and a stale list of items somebody has since
     removed is worse than the cost of rebuilding it. */
  function showConflict(data) {
    var items = (data.conflicts || []).filter(function (c) { return c && c.itemId != null; });
    var wrap = document.createElement('div');
    wrap.className = 'hub-static-conflict';
    wrap.innerHTML =
      '<div class="hub-static-conflict-scrim"></div>' +
      '<div class="hub-static-conflict-panel" role="dialog" aria-modal="true" aria-labelledby="hub-static-conflict-title">' +
        '<h2 class="hub-static-conflict-title" id="hub-static-conflict-title">Someone got there first</h2>' +
        '<p class="hub-static-conflict-lead">' + esc(data.error || 'Part of your cart is being checked out by someone else.') + '</p>' +
        '<ul class="hub-static-conflict-list">' +
          items.map(function (c) {
            return '<li>' + esc(c.title || c.sku || ('Lot ' + (c.lot == null ? '?' : c.lot))) + '</li>';
          }).join('') +
        '</ul>' +
        '<p class="hub-static-conflict-note">They have a few minutes to finish. You can drop these and buy the rest now, or keep them and try again shortly — if they do not finish, the items come back.</p>' +
        '<div class="hub-static-conflict-actions">' +
          '<button type="button" class="hub-static-conflict-go" id="hub-static-conflict-drop">' +
            (items.length === 1 ? 'Remove it and check out' : 'Remove them and check out') +
          '</button>' +
          '<button type="button" class="hub-static-conflict-keep" id="hub-static-conflict-keep">Keep them &amp; wait</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(wrap);

    var close = function () {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
    };
    wrap.querySelector('.hub-static-conflict-scrim').addEventListener('click', close);
    $('hub-static-conflict-keep').addEventListener('click', function () {
      close();
      refresh();
    });
    $('hub-static-conflict-drop').addEventListener('click', function () {
      var go = $('hub-static-conflict-drop');
      if (go) { go.disabled = true; go.textContent = 'Removing…'; }
      /* ⚠️ SEQUENTIALLY, and only then re-try the checkout. Firing the removals
         in parallel and racing the retry against them is how a shopper ends up
         blocked a second time by an item they just dropped. */
      var seq = Promise.resolve();
      items.forEach(function (c) {
        seq = seq.then(function () { return api('DELETE', '/cart/' + encodeURIComponent(c.itemId)); });
      });
      seq.then(function () {
        close();
        return loadCart();
      }).then(function () {
        /* Nothing left to buy — say so rather than firing an empty checkout at
           the server to be told the same thing. */
        if (!cart.lines.length) {
          paintAll();
          toast('Your cart is empty now.', 'info');
          return null;
        }
        return checkout();
      });
    });
  }

  function applyCart(data) {
    if (!data) return;
    cart = {
      lines: data.lines || [],
      dropped: data.dropped || [],
      subtotal: data.subtotal || 0,
      shipping: data.shipping || 0,
      total: data.total || 0,
      shippingIsEstimate: data.shippingIsEstimate !== false,
      expiresAt: data.expiresAt || null
    };
    /* The cart's lines know their catalogue item id; the cards are keyed by
       Shopify product id. Map one onto the other once, here, so paintCard does
       not have to search the sale map for every card on a 200-lot page. */
    var byItem = {};
    Object.keys(sale.lots).forEach(function (pid) { byItem[sale.lots[pid].itemId] = pid; });
    cart.lines.forEach(function (l) { l.productId = byItem[l.itemId] || null; });
  }

  // ===========================================================================
  // LOADING
  // ===========================================================================
  /* A product id as the CARD knows it: bare digits. Every lookup in this file is
     `sale.lots[String(productId)]` where productId comes from `data-static-lot`,
     which Liquid renders as `{{ product.id }}` — numeric.

     ⚠️ THE SERVER USED TO KEY THIS MAP BY GID and it broke the entire shop
     (2026-10-03): `catalogue_items.shopify_product_id` holds
     `gid://shopify/Product/10573524566305`, so every entry was filed under a key
     no card would ever ask for. `entry` was always undefined, every Add button
     stayed `disabled`, and a disabled button makes the click handler return —
     so the press fell through to Dawn's stretched card link and opened the
     product page. It looked exactly like a z-index problem.

     ⚠️ Normalised HERE as well as on the server ON PURPOSE. The script is served
     from Shopify's CDN and the API from Heroku, so the two are deployed
     independently and a browser can hold a cached copy of either for days —
     this makes the pairing order irrelevant in both directions. */
  function normalizeLots(lots) {
    var out = {};
    Object.keys(lots || {}).forEach(function (k) {
      var m = String(k).match(/(\d+)\s*$/);
      out[m ? m[1] : k] = lots[k];
    });
    return out;
  }

  function loadSale() {
    return api('GET', '').then(function (r) {
      if (r.data && r.data.lots) {
        sale.lots = normalizeLots(r.data.lots);
        sale.bySku = r.data.bySku || {};
        sale.shipping = r.data.shipping || sale.shipping;
      }
    });
  }

  function loadCart() {
    if (!signedIn()) return Promise.resolve();
    return api('GET', '/cart').then(function (r) { if (r.ok) applyCart(r.data); });
  }

  var refreshing = null;
  function refresh() {
    // One in flight at a time — an add, a doorbell and a poll can all land together.
    if (refreshing) return refreshing;
    refreshing = Promise.all([loadSale(), loadCart()]).then(function () {
      refreshing = null;
      paintAll();
    }, function () { refreshing = null; });
    return refreshing;
  }

  // ===========================================================================
  // WIRING
  // ===========================================================================
  function wire() {
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-static-add]') : null;
      if (!btn || btn.disabled) return;
      /* ⚠️ THE CARD IS ONE BIG LINK. Dawn stretches the product title's anchor
         across the whole card with an `::after` at inset:0, so a control in the
         card body is fighting it for the click. The CSS wins that fight
         (`.hub-static` and the button are positioned — see note 3 in
         auction-hub-static.css), and these two lines are the belt to that
         braces: if a future card ever nests this inside the anchor, the button
         still adds to the cart instead of navigating to the product page. */
      e.preventDefault();
      e.stopPropagation();
      var pid = btn.getAttribute('data-static-add');
      var entry = sale.lots[String(pid)];
      var inCart = cart.lines.some(function (l) { return String(l.productId || '') === String(pid) || (entry && l.itemId === entry.itemId); });
      if (inCart && entry) removeItem(entry.itemId);
      else addItem(pid);
    });

    /* A doorbell, if a socket happens to be on the page — the payload is only a
       nudge and the state is always re-read, so nothing private rides on it. A
       poll covers the case where there is no socket at all. */
    try {
      if (window.io && MHV && MHV.SERVER_URL) {
        var sock = window.io(MHV.SERVER_URL, { transports: ['websocket', 'polling'] });
        sock.on('static-sale-updated', function (p) {
          if (!p || String(p.code) !== String(CODE)) return;
          refresh();
        });
      }
    } catch (e) { /* no socket is fine — the poll below covers it */ }

    setInterval(function () {
      if (document.hidden) return;
      refresh();
    }, 45000);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) refresh();
    });
  }

  function init() {
    wire();
    refresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
